import { Fragment, type ReactNode } from 'react'
import clsx from 'clsx'

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  align?: 'left' | 'right'
  render: (row: T) => ReactNode
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  getRowKey: (row: T) => string
  /** Key of the row (per `getRowKey`) whose expanded content should render
   * inline, directly below it - PRD 05's accordion row-expand. Omit (with
   * `renderExpanded`) for a plain flat table - existing callers are
   * unaffected. */
  expandedRowKey?: string
  renderExpanded?: (row: T) => ReactNode
}

// Sticky header, left-align text / right-align numeric columns.
export function DataTable<T>({ columns, data, getRowKey, expandedRowKey, renderExpanded }: DataTableProps<T>) {
  return (
    <div className="overflow-x-auto rounded-control border border-border">
      <table className="w-full text-body">
        <thead className="sticky top-0 bg-surface-raised">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={clsx(
                  'px-3 py-2 text-meta font-medium text-text-secondary',
                  column.align === 'right' ? 'text-right' : 'text-left',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            const rowKey = getRowKey(row)
            const isExpanded = renderExpanded && rowKey === expandedRowKey
            return (
              <Fragment key={rowKey}>
                <tr className="border-t border-border">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={clsx(
                        'px-3 py-2 text-text-primary',
                        column.align === 'right' ? 'text-right' : 'text-left',
                      )}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
                {isExpanded && (
                  <tr className="border-t border-border">
                    <td colSpan={columns.length} className="p-0">
                      {renderExpanded(row)}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
